'use client';

import { EcEnv } from '@castlery/config';
import { Box, Button, Stack, Typography, useBreakpoints } from '@castlery/fortress';
import { Communication } from '@castlery/fortress/Icons';
import React, { useState, useEffect } from 'react';
import { GladlyLoadScript } from './gladly-inject';
import { useAppSelector } from '@castlery/shared-redux-store';
import { selectedActiveUser } from '@castlery/modules-user-domain';

const GladlyBox = () => {
  const { mobile } = useBreakpoints();
  const [active, setActive] = useState(false);
  const [hide, setHide] = useState(false);
  const currentUser = useAppSelector(selectedActiveUser);

  useEffect(() => {
    if (EcEnv.NEXT_PUBLIC_COUNTRY === 'SG') {
      setActive(true);
    }
    window?.Gladly?.navigate && window?.Gladly?.navigate();
  }, []);

  useEffect(() => {
    if (active) {
      let attempts = 0;
      const maxAttempts = 10; // 最多尝试 10 秒

      const intervalId = window.setInterval(() => {
        attempts++;

        // 超时保护：如果 10 秒后还没初始化，放弃尝试
        if (attempts > maxAttempts) {
          window.clearInterval(intervalId);
          console.warn('Gladly SDK initialization timeout');
          return;
        }

        try {
          // 更严格的初始化检查
          if (window.Gladly && typeof window.Gladly?.on === 'function' && typeof window.Gladly?.show === 'function') {
            window.Gladly.on('sidekick:opened', () => {
              setHide(true);
            });
            window.Gladly.on('sidekick:closed', () => {
              setHide(false);
            });
            window.clearInterval(intervalId);
          }
        } catch (error) {
          // 捕获并忽略初始化错误，继续尝试
          console.warn('Gladly SDK not fully initialized yet:', error);
        }
      }, 1000);

      return () => {
        window.clearInterval(intervalId);
      };
    }
  }, [active]);

  return (
    <>
      <GladlyLoadScript
        handleOnReady={() => {
          setActive(true);
        }}
      />
      {active && (
        <Box
          sx={[
            {
              position: 'fixed',
              backgroundColor: (theme) => theme.palette.brand.terracotta[500],
              zIndex: 999,
              right: 0,
              width: '56px',
              height: '70px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, .5)',
              bottom: mobile ? '110px' : '80px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '8px 12px',
              opacity: hide ? 0 : 1,
              '.gladly-unread': {
                '#with-unread-message': {
                  display: 'block',
                },
              },
            },
          ]}
        >
          <Button
            sx={{
              width: '100%',
              backgroundColor: 'transparent',
              borderRightWidth: 0,
              borderRadius: 0,
              position: 'relative',
              border: 'none',
              color: (theme) => theme.palette.brand.terracotta[500],
              padding: '8px 12px',
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
            id="custom-gladly-chat-button"
            aria-label="Chat"
            onClick={() => {
              if (EcEnv.NEXT_PUBLIC_COUNTRY === 'SG') {
                window.location.href = 'https://wa.me/6582410030';
              } else {
                window?.Gladly?.show?.();
                if (currentUser) {
                  const name = `${currentUser.firstname || ''} ${currentUser.lastname || ''}`;
                  const email = currentUser.email;
                  const _appData =
                    localStorage.getItem(`castlery.com-${EcEnv.NEXT_PUBLIC_COUNTRY.toLocaleLowerCase()}`) || '';
                  const appData = JSON.parse(_appData);
                  if (appData?.values?.user?.email === currentUser.email && appData?.values?.user?.name === name) {
                    return;
                  }
                  window.Gladly?.setUser &&
                    window.Gladly?.setUser({
                      name,
                      email,
                    });
                }
              }
            }}
          >
            <Box
              sx={[
                {
                  position: 'absolute',
                  background: (theme) => theme.palette.brand.terracotta[500],
                  border: '2px solid #fff',
                  borderRadius: '50%',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, .5)',
                  top: '4px',
                  left: '14px',
                  height: '10px',
                  width: '10px',
                  display: 'none',
                },
                mobile && {
                  top: '1px',
                  left: '5px',
                },
              ]}
              id="with-unread-message"
            />
            <Stack
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Stack
                sx={{
                  alignItems: 'center',
                  justifyContent: 'center',
                  paddingBottom: '4px',
                }}
              >
                <Communication
                  sx={[
                    {
                      width: '24px !important',
                      height: '22.77px !important',
                    },
                  ]}
                />
              </Stack>
              <Typography
                level="body2"
                sx={{
                  textTransform: 'capitalize !important',
                  color: (theme) => theme.palette.brand.warmLinen[500],
                }}
              >
                Chat
              </Typography>
            </Stack>
          </Button>
        </Box>
      )}
    </>
  );
};

export { GladlyBox };
